import * as z from 'zod';

export const ListAccountIdsRequest = z.object({
  instanceId: z
    .string()
    .describe('Instance ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const ListAccountIdsResponse = z.object({
  accountIds: z.array(z.string()).optional(),
});
export const BusinessStatusRequest = z.object({
  instanceId: z
    .string()
    .describe('Instance ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({ accountId: z.string().describe('Account ID.') }),
});
export const BusinessStatusResponse = z.object({
  status: z
    .enum(['NONE', 'READY', 'PENDING', 'ERROR', 'NOT_STARTED'])
    .describe('List of statue.')
    .optional(),
  failureReasonMessage: z
    .string()
    .describe('The reason for the error in free text.')
    .optional()
    .nullable(),
});
export const EstimateDeliveryRequest = z.object({
  instanceId: z
    .string()
    .describe('Instance ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.intersection(
    z.object({
      accountId: z.string().describe('Account ID.'),
      pickupAddress: z
        .object({
          country: z.string().describe('Country code.').optional(),
          subdivision: z
            .string()
            .describe(
              'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
            )
            .optional()
            .nullable(),
          city: z.string().describe('City name.').optional(),
          postalCode: z
            .string()
            .describe('Zip/postal code.')
            .optional()
            .nullable(),
          streetAddress: z
            .object({
              number: z.string().describe('Street number.').optional(),
              name: z.string().describe('Street name.').optional(),
            })
            .describe('Street name and number.')
            .optional(),
          addressLine2: z
            .string()
            .describe(
              'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
            )
            .optional()
            .nullable(),
          geocode: z
            .object({
              latitude: z
                .number()
                .describe('Address latitude.')
                .optional()
                .nullable(),
              longitude: z
                .number()
                .describe('Address longitude.')
                .optional()
                .nullable(),
            })
            .describe('Coordinates of the physical address.')
            .optional(),
        })
        .describe('Delivery pickup address.')
        .optional(),
      dropoffAddress: z
        .object({
          country: z.string().describe('Country code.').optional(),
          subdivision: z
            .string()
            .describe(
              'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
            )
            .optional()
            .nullable(),
          city: z.string().describe('City name.').optional(),
          postalCode: z
            .string()
            .describe('Zip/postal code.')
            .optional()
            .nullable(),
          streetAddress: z
            .object({
              number: z.string().describe('Street number.').optional(),
              name: z.string().describe('Street name.').optional(),
            })
            .describe('Street name and number.')
            .optional(),
          addressLine2: z
            .string()
            .describe(
              'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
            )
            .optional()
            .nullable(),
          geocode: z
            .object({
              latitude: z
                .number()
                .describe('Address latitude.')
                .optional()
                .nullable(),
              longitude: z
                .number()
                .describe('Address longitude.')
                .optional()
                .nullable(),
            })
            .describe('Coordinates of the physical address.')
            .optional(),
        })
        .describe('Delivery dropoff address.')
        .optional(),
      storeLocationId: z
        .string()
        .describe(
          'Unique identifier of the specific store that the delivery is for.'
        ),
      orderTotal: z
        .object({
          value: z
            .string()
            .describe(
              'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
            )
            .optional(),
          currency: z
            .string()
            .describe(
              'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
            )
            .optional(),
          formattedValue: z
            .string()
            .describe(
              'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'The subtotal for all items in the order, excluding tax/tip.'
        ),
    }),
    z.xor([
      z.object({
        dropoffTime: z.never().optional(),
        pickupTime: z.date().describe('Estimated pickup time.'),
      }),
      z.object({
        pickupTime: z.never().optional(),
        dropoffTime: z.date().describe('Estimated dropoff time.'),
      }),
    ])
  ),
});
export const EstimateDeliveryResponse = z.object({
  _id: z
    .string()
    .describe(
      'Optional. Used as reference to the estimate when creating a new order.'
    )
    .optional()
    .nullable(),
  fee: z
    .object({
      value: z
        .string()
        .describe(
          'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
        )
        .optional(),
      currency: z
        .string()
        .describe(
          'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
        )
        .optional(),
      formattedValue: z
        .string()
        .describe(
          'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
        )
        .optional()
        .nullable(),
    })
    .describe('Amount that is charged for this delivery.')
    .optional(),
  promisedPickupTime: z
    .date()
    .describe('Estimated pickup time.')
    .optional()
    .nullable(),
  promisedDropoffTime: z
    .date()
    .describe('Estimated dropoff time.')
    .optional()
    .nullable(),
});
export const CreateDeliveryRequest = z.object({
  instanceId: z
    .string()
    .describe('Instance ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.intersection(
    z.object({
      accountId: z.string().describe('Account ID.'),
      storeLocationId: z
        .string()
        .describe(
          'Unique identifier of the specific store that the delivery is for.'
        ),
      pickupBusinessName: z
        .string()
        .describe(
          'The name of the business where the order should be picked up from.'
        ),
      estimateId: z.string().describe('Estimate ID.').optional().nullable(),
      pickupAddress: z
        .object({
          country: z.string().describe('Country code.').optional(),
          subdivision: z
            .string()
            .describe(
              'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
            )
            .optional()
            .nullable(),
          city: z.string().describe('City name.').optional(),
          postalCode: z
            .string()
            .describe('Zip/postal code.')
            .optional()
            .nullable(),
          streetAddress: z
            .object({
              number: z.string().describe('Street number.').optional(),
              name: z.string().describe('Street name.').optional(),
            })
            .describe('Street name and number.')
            .optional(),
          addressLine2: z
            .string()
            .describe(
              'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
            )
            .optional()
            .nullable(),
          geocode: z
            .object({
              latitude: z
                .number()
                .describe('Address latitude.')
                .optional()
                .nullable(),
              longitude: z
                .number()
                .describe('Address longitude.')
                .optional()
                .nullable(),
            })
            .describe('Coordinates of the physical address.')
            .optional(),
        })
        .describe('Delivery pickup address.'),
      dropoffAddress: z
        .object({
          country: z.string().describe('Country code.').optional(),
          subdivision: z
            .string()
            .describe(
              'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
            )
            .optional()
            .nullable(),
          city: z.string().describe('City name.').optional(),
          postalCode: z
            .string()
            .describe('Zip/postal code.')
            .optional()
            .nullable(),
          streetAddress: z
            .object({
              number: z.string().describe('Street number.').optional(),
              name: z.string().describe('Street name.').optional(),
            })
            .describe('Street name and number.')
            .optional(),
          addressLine2: z
            .string()
            .describe(
              'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
            )
            .optional()
            .nullable(),
          geocode: z
            .object({
              latitude: z
                .number()
                .describe('Address latitude.')
                .optional()
                .nullable(),
              longitude: z
                .number()
                .describe('Address longitude.')
                .optional()
                .nullable(),
            })
            .describe('Coordinates of the physical address.')
            .optional(),
        })
        .describe('Delivery dropoff address.'),
      pickupPhoneNumber: z
        .string()
        .describe(
          'Phone number of the store where the order should be picked up from.'
        ),
      customer: z
        .object({
          firstName: z.string().describe('First name.'),
          lastName: z.string().describe('Last name.'),
          phoneNumber: z.string().describe('Phone number.'),
          email: z.string().describe('Email address.').email(),
        })
        .describe('Customer details.'),
      pickupInstructions: z
        .string()
        .describe('Instructions for the courier at the pickup location.')
        .optional()
        .nullable(),
      dropoffInstructions: z
        .string()
        .describe('Instructions for the courier at the dropoff location.')
        .optional()
        .nullable(),
      contactlessDelivery: z
        .boolean()
        .describe('Is contactless delivery.')
        .optional()
        .nullable(),
      orderId: z.string().describe('Order ID.'),
      orderTotal: z
        .object({
          value: z
            .string()
            .describe(
              'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
            )
            .optional(),
          currency: z
            .string()
            .describe(
              'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
            )
            .optional(),
          formattedValue: z
            .string()
            .describe(
              'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'The subtotal for all items in the order, excluding tax/tip.'
        ),
      orderTip: z
        .object({
          value: z
            .string()
            .describe(
              'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
            )
            .optional(),
          currency: z
            .string()
            .describe(
              'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
            )
            .optional(),
          formattedValue: z
            .string()
            .describe(
              'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
            )
            .optional()
            .nullable(),
        })
        .describe('Amount that will be paid to the courier.'),
    }),
    z.xor([
      z.object({
        dropoffTime: z.never().optional(),
        pickupTime: z
          .date()
          .describe(
            'The requested time at which the order should be picked up.'
          ),
      }),
      z.object({
        pickupTime: z.never().optional(),
        dropoffTime: z
          .date()
          .describe(
            'The requested time at which the order should be delivered.'
          ),
      }),
    ])
  ),
});
export const CreateDeliveryResponse = z.object({
  _id: z.string().describe('The vendor new order id').optional(),
  pickupWindowStartTime: z
    .date()
    .describe(
      'When a delivery is ready to be picked up. This is the start of the pickup window.'
    )
    .optional()
    .nullable(),
  pickupWindowEndTime: z
    .date()
    .describe(
      'When a delivery must be picked up by. This is the end of the pickup window.'
    )
    .optional()
    .nullable(),
  dropoffWindowStartTime: z
    .date()
    .describe(
      'When a delivery is ready to be dropped off. This is the start of the dropoff window.'
    )
    .optional()
    .nullable(),
  dropoffWindowEndTime: z
    .date()
    .describe(
      'When a delivery must be dropped off by. This is the end of the dropoff window.'
    )
    .optional()
    .nullable(),
  fee: z
    .object({
      value: z
        .string()
        .describe(
          'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
        )
        .optional(),
      currency: z
        .string()
        .describe(
          'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
        )
        .optional(),
      formattedValue: z
        .string()
        .describe(
          'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
        )
        .optional()
        .nullable(),
    })
    .describe('Amount that is charged for this delivery.')
    .optional(),
});
